import React, { useState } from 'react';
import { Check } from 'lucide-react';

const CODE = 'JUNE';

/**
 * Floating mascot anchored to the bottom-left of the screen.
 * Permanent — cannot be dismissed by the user (continuous reminder).
 * The 3D Chancellor character holds a gold placard; the discount code
 * is rendered directly ON the placard he is holding, with a flashing
 * + blinking animation. Click anywhere on the figure to copy the code.
 */
const JuneCodeMascot = () => {
  const [copied, setCopied] = useState(false);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(CODE);
      setCopied(true);
      setTimeout(() => setCopied(false), 2200);
    } catch {
      // ignore — user can still read & type the code
    }
  };

  return (
    <>
      <style>{`
        @keyframes tcaMascotIn {
          from { opacity: 0; transform: translateY(40px) scale(0.85); }
          to   { opacity: 1; transform: translateY(0) scale(1); }
        }
        @keyframes tcaMascotIdle {
          0%, 100% { transform: translateY(0) rotate(0deg); }
          50%      { transform: translateY(-6px) rotate(-0.6deg); }
        }
        @keyframes tcaCodeBlink {
          0%, 65%, 100% {
            opacity: 1;
            text-shadow:
              0 0 8px rgba(255, 255, 255, 0.85),
              0 0 14px rgba(212, 175, 55, 0.55);
          }
          75%, 85% {
            opacity: 0.45;
            text-shadow: 0 0 4px rgba(255, 255, 255, 0.25);
          }
        }
        @keyframes tcaPlacardGlow {
          0%, 100% { filter: drop-shadow(0 0 0 rgba(212, 175, 55, 0.0)); }
          50%      { filter: drop-shadow(0 0 14px rgba(255, 232, 130, 0.85)); }
        }
        @keyframes tcaSparkle {
          0%, 100% { opacity: 0; transform: scale(0.4) rotate(0deg); }
          50%      { opacity: 1; transform: scale(1) rotate(180deg); }
        }

        .tca-mascot-wrap {
          position: fixed;
          left: 16px;
          bottom: 12px;
          z-index: 70;
          pointer-events: none;
          animation: tcaMascotIn 800ms cubic-bezier(0.22, 1, 0.36, 1) both;
        }

        .tca-mascot-btn {
          position: relative;
          background: none;
          border: none;
          padding: 0;
          margin: 0;
          cursor: pointer;
          pointer-events: auto;
          display: block;
          animation: tcaMascotIdle 4.2s ease-in-out infinite;
          transform-origin: bottom center;
          transition: transform 380ms cubic-bezier(0.22, 1, 0.36, 1);
        }
        .tca-mascot-btn:hover { transform: scale(1.04); }

        .tca-mascot-figure {
          width: 86px;
          height: auto;
          display: block;
          filter: drop-shadow(0 16px 20px rgba(0, 0, 0, 0.55));
          animation: tcaPlacardGlow 2.2s ease-in-out infinite;
        }

        /* Discount code overlay — positioned ON the placard the mascot holds */
        .tca-mascot-code-overlay {
          position: absolute;
          top: 4%;
          left: 12%;
          right: 12%;
          height: 20%;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          color: #1a0a2e;
          text-align: center;
          pointer-events: none;
          line-height: 1;
          gap: 1px;
        }
        .tca-mascot-code-overlay-get {
          font-family: 'Inter', system-ui, sans-serif;
          font-size: 8px;
          font-weight: 800;
          letter-spacing: 0.28em;
          text-transform: uppercase;
          color: #1a0a2e;
          opacity: 0.85;
          line-height: 1;
        }
        .tca-mascot-code-overlay-pct {
          font-family: 'Cormorant Garamond', Georgia, serif;
          font-style: italic;
          font-weight: 700;
          font-size: 20px;
          letter-spacing: 0.02em;
          color: #1a0a2e;
          line-height: 1;
          margin-top: 2px;
          animation: tcaCodeBlink 1.6s ease-in-out infinite;
        }

        .tca-mascot-sparkle {
          position: absolute;
          top: 1%;
          right: 8%;
          width: 14px;
          height: 14px;
          background: radial-gradient(circle, #fff7d6 0%, #d4af37 60%, transparent 70%);
          border-radius: 50%;
          animation: tcaSparkle 1.8s ease-in-out infinite;
          pointer-events: none;
        }
        .tca-mascot-sparkle.s2 {
          top: 4%;
          left: 8%;
          right: auto;
          width: 10px;
          height: 10px;
          animation-delay: 0.9s;
        }

        .tca-mascot-toast {
          position: absolute;
          left: 50%;
          top: -34px;
          transform: translateX(-50%);
          background: #1a0a2e;
          color: #d4af37;
          font-family: 'Inter', system-ui, sans-serif;
          font-size: 10px;
          font-weight: 700;
          letter-spacing: 0.2em;
          text-transform: uppercase;
          padding: 8px 12px;
          border-radius: 6px;
          border: 1px solid rgba(212, 175, 55, 0.55);
          white-space: nowrap;
          display: inline-flex;
          align-items: center;
          gap: 6px;
          animation: tcaMascotIn 320ms ease-out both;
          pointer-events: none;
          z-index: 4;
        }

        @media (max-width: 720px) {
          .tca-mascot-figure { width: 82px; }
          .tca-mascot-code-overlay-pct { font-size: 12px; }
          .tca-mascot-code-overlay-get { font-size: 6.5px; }
          .tca-mascot-wrap { left: 8px; bottom: 8px; }
        }
      `}</style>

      <div className="tca-mascot-wrap" data-testid="june-mascot">
        <button
          type="button"
          className="tca-mascot-btn"
          onClick={copy}
          aria-label={`Tap to copy discount code ${CODE}`}
          data-testid="june-mascot-btn"
        >
          {copied && (
            <span className="tca-mascot-toast" role="status">
              <Check size={12} /> Copied!
            </span>
          )}
          <img
            src="/brand/mascot-june.png"
            alt="The Collins Academy mascot holding a 50% off discount sign"
            className="tca-mascot-figure"
            draggable={false}
          />
          {/* Animated discount text rendered ON the placard he's holding */}
          <div className="tca-mascot-code-overlay" aria-hidden="true">
            <span className="tca-mascot-sparkle" />
            <span className="tca-mascot-sparkle s2" />
            <div className="tca-mascot-code-overlay-get">Get</div>
            <div className="tca-mascot-code-overlay-pct">50% Off</div>
          </div>
        </button>
      </div>
    </>
  );
};

export default JuneCodeMascot;
